import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section, SectionHeader } from "../components/Section";
import { CTA } from "../components/CTA";
import { useIdentity } from "../lib/identity";
import { LICENSE_TIERS, type LicenseTierId } from "../lib/licensing";
import { useTranslation } from "../lib/i18n";

function CheckIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "0.125rem" }}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

const INDUSTRIES = [
  "Healthcare",
  "Defense",
  "Transportation",
  "Manufacturing",
  "Municipal Agencies",
  "Retail",
  "Hospitality",
  "Education",
  "Cyber/IT",
  "OEMs and Vendors",
  "Other",
];

const TIER_OPTIONS: { id: LicenseTierId; name: string }[] = [
  { id: "use", name: "Sentinel Use License" },
  { id: "architecture", name: "Sentinel Architecture License" },
  { id: "template", name: "Sentinel Template License" },
  { id: "certification", name: "Sentinel Certification License" },
  { id: "automation", name: "Sentinel Automation License" },
  { id: "vendor_integration", name: "Sentinel Vendor Integration License" },
  { id: "organizational", name: "Sentinel Organizational Certification" },
];

const CONSULTATION_BENEFITS = [
  "Licensing Needs Assessment — We review your organization's structure, departments, workflows, and compliance requirements.",
  "Industry-Specific Licensing Guidance — Tailored licensing plan for your industry.",
  "Department & Budget Mapping — Determine which departments need licensing, seat counts, and budget separation.",
  "Enterprise Architecture Review — Identify which licenses you need (Use, Architecture, Template, Certification, Automation, Vendor Integration).",
  "Pricing Guidance — Public pricing for small teams, group pricing, department pricing, enterprise quote ranges, and vendor/OEM structure.",
  "Deployment Roadmap — Certification rollout, department onboarding, enterprise compliance setup, automation integration, and Organizational Certification pathway.",
];

export default function QuoteRequestPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { identity } = useIdentity();
  const { t } = useTranslation();
  const tierParam = searchParams.get("tier") as LicenseTierId | null;
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const [selectedTiers, setSelectedTiers] = useState<Set<LicenseTierId>>(new Set());

  const [form, setForm] = useState({
    orgName: "",
    ein: "",
    industry: "",
    numDepartments: "",
    numEmployees: "",
    contactName: "",
    email: "",
    deptCount: "",
    seatsPerDept: "",
    deptsIndependent: false,
    deptsSeparateBudgets: false,
    automationLevel: "None",
    iotIntegration: false,
    roboticsIntegration: false,
    softwareIntegration: false,
    oemIntegration: false,
    annualAudit: false,
    multiFacility: false,
    govRequirements: false,
    vendorCertification: false,
    notes: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (tierParam && TIER_OPTIONS.some((t) => t.id === tierParam)) {
      setSelectedTiers(new Set([tierParam]));
    }
    if (identity.email) {
      setForm((f) => ({ ...f, email: identity.email ?? "" }));
    }
    if (identity.name) {
      setForm((f) => ({ ...f, contactName: identity.name ?? "" }));
    }
  }, [tierParam, identity.email, identity.name]);

  const update = (field: string, value: string | boolean) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  function toggleTier(id: LicenseTierId) {
    setSelectedTiers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.orgName.trim() || !form.contactName.trim() || !form.email.trim()) {
      setError(t("quote.errorOrgName"));
      scrollToTop();
      return;
    }
    if (selectedTiers.size === 0) {
      setError(t("quote.errorTiers"));
      scrollToTop();
      return;
    }
    setStatus("submitting");
    setError(null);

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const payload = {
      org_name: form.orgName,
      ein: form.ein,
      industry: form.industry,
      num_departments: form.numDepartments,
      num_employees: form.numEmployees,
      contact_name: form.contactName,
      email: form.email,
      selected_tiers: Array.from(selectedTiers),
      dept_count: form.deptCount,
      seats_per_dept: form.seatsPerDept,
      depts_independent: form.deptsIndependent,
      depts_separate_budgets: form.deptsSeparateBudgets,
      automation_level: form.automationLevel,
      iot_integration: form.iotIntegration,
      robotics_integration: form.roboticsIntegration,
      software_integration: form.softwareIntegration,
      oem_integration: form.oemIntegration,
      annual_audit: form.annualAudit,
      multi_facility: form.multiFacility,
      gov_requirements: form.govRequirements,
      vendor_certification: form.vendorCertification,
      notes: form.notes,
      user_id: identity.userId ?? null,
    };

    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/licensing-quote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error ?? `Request failed (${res.status})`);
      }
      setStatus("success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit quote request.");
      setStatus("error");
      scrollToTop();
    }
  }

  const tierLabel = tierParam ? LICENSE_TIERS.find((t) => t.id === tierParam)?.name : null;

  return (
    <>
      <SEO
        title="Licensing Consultation — Sentinel"
        description="Request a Sentinel licensing consultation. We'll review your organization's structure, departments, workflows, and compliance requirements to build a tailored licensing plan."
        canonical="/licensing/quote"
      />
      <PageHero
        eyebrow={t("quote.eyebrow")}
        title={tierLabel ? t("quote.titleWithTier", { name: tierLabel }) : t("quote.title")}
        description={t("quote.description")}
        bgVariant="burgundy"
        bgImage="https://images.pexels.com/photos/7841502/pexels-photo-7841502.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      {/* What You Get */}
      <Section>
        <SectionHeader
          eyebrow={t("quote.whatYouGetEyebrow")}
          title={t("quote.whatYouGetTitle")}
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "0.75rem" }}>
          {[t("quote.benefit1"), t("quote.benefit2"), t("quote.benefit3"), t("quote.benefit4"), t("quote.benefit5"), t("quote.benefit6")].map((item) => (
            <div key={item} className="card" style={{ padding: "1rem", display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
              <span style={{ color: "var(--accent-600)", flexShrink: 0 }}><CheckIcon size={16} /></span>
              <span style={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>{item}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Intake Form */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("quote.formEyebrow")}
          title={t("quote.formTitle")}
          description={t("quote.formDescription")}
        />

        {status === "success" ? (
          <div className="card" style={{ padding: "var(--space-6)", textAlign: "center", borderColor: "var(--accent-400)", background: "var(--accent-50)" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>&#10003;</div>
            <h3 style={{ marginBottom: "0.5rem" }}>{t("quote.successTitle")}</h3>
            <p className="text-muted" style={{ marginBottom: "1.5rem", maxWidth: "480px", margin: "0 auto 1.5rem" }}>
              {t("quote.successBody")}
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
              <CTA to="/licensing" variant="primary">{t("quote.backToLicensing")}</CTA>
              <CTA to="/" variant="secondary">{t("quote.homepage")}</CTA>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ maxWidth: "720px", margin: "0 auto" }}>
            {error && (
              <div className="card" style={{ padding: "1rem 1.5rem", marginBottom: "1rem", borderColor: "var(--error-400)", background: "var(--error-50)", color: "var(--error-700)", fontWeight: 600, fontSize: "0.875rem" }}>
                {error}
              </div>
            )}

            {/* Section 1: Organization Information */}
            <div className="card" style={{ padding: "1.5rem", marginBottom: "1rem" }}>
              <h3 style={{ fontSize: "1rem", marginBottom: "1rem", paddingBottom: "0.75rem", borderBottom: "1px solid var(--border)" }}>
                {t("quote.orgInfoTitle")}
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="orgName">{t("quote.orgName")}</label>
                  <input id="orgName" type="text" className="form-input" value={form.orgName} onChange={(e) => update("orgName", e.target.value)} placeholder="Acme Corporation" />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="ein">{t("quote.ein")}</label>
                  <input id="ein" type="text" className="form-input" value={form.ein} onChange={(e) => update("ein", e.target.value)} placeholder="12-3456789" />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="industry">{t("quote.industry")}</label>
                  <select id="industry" className="form-input" value={form.industry} onChange={(e) => update("industry", e.target.value)}>
                    <option value="">{t("quote.selectIndustry")}</option>
                    {INDUSTRIES.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="numDepartments">{t("quote.numDepartments")}</label>
                  <input id="numDepartments" type="number" className="form-input" value={form.numDepartments} onChange={(e) => update("numDepartments", e.target.value)} placeholder="5" min="1" />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="numEmployees">{t("quote.numEmployees")}</label>
                  <input id="numEmployees" type="number" className="form-input" value={form.numEmployees} onChange={(e) => update("numEmployees", e.target.value)} placeholder="250" min="1" />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="contactName">{t("quote.contactName")}</label>
                  <input id="contactName" type="text" className="form-input" value={form.contactName} onChange={(e) => update("contactName", e.target.value)} placeholder="Jane Smith" />
                </div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label" htmlFor="email">{t("quote.email")}</label>
                  <input id="email" type="email" className="form-input" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@acme.com" />
                </div>
              </div>
            </div>

            {/* Section 2: Licensing Needs */}
            <div className="card" style={{ padding: "1.5rem", marginBottom: "1rem" }}>
              <h3 style={{ fontSize: "1rem", marginBottom: "1rem", paddingBottom: "0.75rem", borderBottom: "1px solid var(--border)" }}>
                {t("quote.needsTitle")}
              </h3>
              <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "0.75rem" }}>{t("quote.needsDescription")}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {TIER_OPTIONS.map((tier) => (
                  <label key={tier.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem", cursor: "pointer", padding: "0.5rem 0.75rem", borderRadius: "var(--radius-sm)", background: selectedTiers.has(tier.id) ? "var(--accent-50)" : "transparent", border: `1px solid ${selectedTiers.has(tier.id) ? "var(--accent-200)" : "var(--border)"}` }}>
                    <input
                      type="checkbox"
                      checked={selectedTiers.has(tier.id)}
                      onChange={() => toggleTier(tier.id)}
                      style={{ width: "1rem", height: "1rem" }}
                    />
                    <span>{tier.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Section 3: Department-Level Details */}
            <div className="card" style={{ padding: "1.5rem", marginBottom: "1rem" }}>
              <h3 style={{ fontSize: "1rem", marginBottom: "1rem", paddingBottom: "0.75rem", borderBottom: "1px solid var(--border)" }}>
                {t("quote.deptTitle")}
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="deptCount">{t("quote.deptCount")}</label>
                  <input id="deptCount" type="number" className="form-input" value={form.deptCount} onChange={(e) => update("deptCount", e.target.value)} placeholder="3" min="1" />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="seatsPerDept">{t("quote.seatsPerDept")}</label>
                  <input id="seatsPerDept" type="number" className="form-input" value={form.seatsPerDept} onChange={(e) => update("seatsPerDept", e.target.value)} placeholder="25" min="1" />
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.75rem" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem", cursor: "pointer" }}>
                  <input type="checkbox" checked={form.deptsIndependent} onChange={(e) => update("deptsIndependent", e.target.checked)} style={{ width: "1rem", height: "1rem" }} />
                  {t("quote.deptsIndependent")}
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem", cursor: "pointer" }}>
                  <input type="checkbox" checked={form.deptsSeparateBudgets} onChange={(e) => update("deptsSeparateBudgets", e.target.checked)} style={{ width: "1rem", height: "1rem" }} />
                  {t("quote.deptsSeparateBudgets")}
                </label>
              </div>
            </div>

            {/* Section 4: Automation & Integration */}
            <div className="card" style={{ padding: "1.5rem", marginBottom: "1rem" }}>
              <h3 style={{ fontSize: "1rem", marginBottom: "1rem", paddingBottom: "0.75rem", borderBottom: "1px solid var(--border)" }}>
                {t("quote.automationTitle")}
              </h3>
              <div className="form-group">
                <label className="form-label" htmlFor="automationLevel">{t("quote.automationLevel")}</label>
                <select id="automationLevel" className="form-input" value={form.automationLevel} onChange={(e) => update("automationLevel", e.target.value)}>
                  <option value="None">None</option>
                  <option value="Partial">Partial</option>
                  <option value="Full">Full</option>
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginTop: "0.75rem" }}>
                {[
                  { field: "iotIntegration", label: t("quote.iotIntegration") },
                  { field: "roboticsIntegration", label: t("quote.roboticsIntegration") },
                  { field: "softwareIntegration", label: t("quote.softwareIntegration") },
                  { field: "oemIntegration", label: t("quote.oemIntegration") },
                ].map((item) => (
                  <label key={item.field} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem", cursor: "pointer" }}>
                    <input type="checkbox" checked={form[item.field as keyof typeof form] as boolean} onChange={(e) => update(item.field, e.target.checked)} style={{ width: "1rem", height: "1rem" }} />
                    {item.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Section 5: Compliance Requirements */}
            <div className="card" style={{ padding: "1.5rem", marginBottom: "1rem" }}>
              <h3 style={{ fontSize: "1rem", marginBottom: "1rem", paddingBottom: "0.75rem", borderBottom: "1px solid var(--border)" }}>
                {t("quote.complianceTitle")}
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                {[
                  { field: "annualAudit", label: t("quote.annualAudit") },
                  { field: "multiFacility", label: t("quote.multiFacility") },
                  { field: "govRequirements", label: t("quote.govRequirements") },
                  { field: "vendorCertification", label: t("quote.vendorCertification") },
                ].map((item) => (
                  <label key={item.field} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem", cursor: "pointer" }}>
                    <input type="checkbox" checked={form[item.field as keyof typeof form] as boolean} onChange={(e) => update(item.field, e.target.checked)} style={{ width: "1rem", height: "1rem" }} />
                    {item.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Section 6: Additional Notes */}
            <div className="card" style={{ padding: "1.5rem", marginBottom: "1.5rem" }}>
              <h3 style={{ fontSize: "1rem", marginBottom: "1rem", paddingBottom: "0.75rem", borderBottom: "1px solid var(--border)" }}>
                {t("quote.notesTitle")}
              </h3>
              <div className="form-group">
                <label className="form-label" htmlFor="notes">{t("quote.notesLabel")}</label>
                <textarea id="notes" className="form-input" value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={4} placeholder={t("quote.notesPlaceholder")} />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg btn-block"
              disabled={status === "submitting"}
            >
              {status === "submitting" ? t("quote.submitting") : t("quote.submit")}
            </button>
          </form>
        )}
      </Section>

      {status !== "success" && (
        <Section>
          <div style={{ textAlign: "center" }}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: "transparent", border: "none", color: "var(--text-muted)", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.375rem" }}
            >
              &larr; {t("quote.previousPage")}
            </button>
          </div>
        </Section>
      )}
    </>
  );
}
